import {
  useState,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  memo,
  type ReactNode,
} from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItemWithSubmenu,
} from '@/shared/components';
import { ActionMenuItem } from './ActionMenuItem';
import { ActionMenuItemContent } from './ActionMenuItemContent';
import { AccountRowContent } from './AccountRowContent';
import { MenuSeparator } from './MenuSeparator';
import {
  MENU_CONTENT_CLASSNAME,
  MENU_ITEM_CLASSNAME,
  MENU_ITEM_CONTENT_CLASSNAME,
  MENU_ITEM_ICON_CLASSNAME,
  MENU_ITEM_LABEL_CLASSNAME,
  MENU_VIEWPORT_CLASSNAME,
  SUBMENU_CLASSNAME,
} from './menuClassNames';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
} from 'lucide-react';
import ArrowDownIcon from '@/shared/assets/arrow-down-icon.svg?react';
import ArrowDownUpIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import ArrowUpRightIcon from '@/shared/assets/arrow-top-right-icon.svg?react';
import PlusIcon from '@/shared/assets/plus-icon.svg?react';
import RefreshIcon from '@/shared/assets/exchange-process-icon.svg?react';
import ApiTokensIcon from '@/shared/assets/api-icon.svg?react';
import OpenNewTabRoundedIcon from '@/shared/assets/open-new-tab-rounded-icon.svg?react';
import LogoSmallIcon from '@/shared/assets/f2ex-logo-small.svg?react';
import type { AccountDetailsActionButtonsVisibility } from '@/widgets/account-details';
import { openTradingAccUrl, useMediaQuery } from '@/shared/lib';
import type {
  AccountMarginRest,
  AccountRest,
  AccountType,
} from '@/entities/account';
import CandlesIcon from '@/shared/assets/candles-icon.svg?react';
import { cn } from '@/shared/utils/styling';
import { useAccountRows, type AccountRow } from '../model/useAccountRows';
import { getFitSideOffset } from '../lib/getFitSideOffset';

const TRIGGER_CLASSNAME =
  'cursor-pointer p-0 relative top-[-1.2rem] right-[-0.8rem] dark:bg-transparent hover:bg-stroke-bg dark:hover:bg-stroke-bg data-[state=open]:bg-stroke-bg dark:data-[state=open]:bg-stroke-bg rounded-full w-[4.8rem] h-[4.8rem] flex items-center justify-center';

const ACCOUNTS_SUBMENU = { className: 'w-[26rem]', widthRem: 26 };

const WALLET_TRANSFER_SUBMENU = { className: 'w-[15.7rem]', widthRem: 15.7 };

type MenuAction = {
  key: string;
  visible: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  labelKey: string;
  onClick: () => void;
};

type Props = {
  type: AccountType;
  accountTitle: string;
  actionButtonsVisibility: AccountDetailsActionButtonsVisibility;
  onDeposit: () => void;
  onWithdraw: () => void;
  onInternalTransfer: (direction?: 'from' | 'to') => void;
  onAccountTransfer: (accountNumber: string) => void;
  onOpenNewAccount: () => void;
  onExchange: () => void;
  onAccountClick: (accountNumber: string) => void;
  accounts: Array<AccountRest | AccountMarginRest>;
  currency: string;
  precision: number;
  isBalancesHidden: boolean;
  triggerClassName?: string;
};

type AccountSubmenuProps = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isMobile: boolean;
  submenuClassName: string;
  submenuWidthRem: number;
  children: ReactNode;
};

const AccountSubmenu = ({
  icon,
  label,
  isMobile,
  submenuClassName,
  submenuWidthRem,
  children,
}: AccountSubmenuProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [sideOffset, setSideOffset] = useState<number>();

  const ChevronIcon = isMobile ? ChevronDownIcon : ChevronRightIcon;

  const updateSideOffset = useCallback(() => {
    setSideOffset(
      isMobile
        ? getFitSideOffset(
            anchorRef.current?.closest('[role="menuitem"]'),
            submenuWidthRem
          )
        : undefined
    );
  }, [isMobile, submenuWidthRem]);

  useLayoutEffect(() => {
    updateSideOffset();
  }, [updateSideOffset]);

  return (
    <DropdownMenuItemWithSubmenu
      className={cn(MENU_ITEM_CLASSNAME, 'pr-[0.8rem]')}
      onClick={e => e.stopPropagation()}
      sideOffset={sideOffset}
      renderTrigger={() => (
        <div
          ref={anchorRef}
          className="w-full"
          onPointerDown={updateSideOffset}>
          <ActionMenuItemContent
            icon={icon}
            className={MENU_ITEM_CONTENT_CLASSNAME}
            iconClassName={MENU_ITEM_ICON_CLASSNAME}
            labelClassName={MENU_ITEM_LABEL_CLASSNAME}
            label={label}>
            <div className="flex items-center justify-center size-[1.6rem]">
              <ChevronIcon className="size-full text-tertiary stroke-[3]" />
            </div>
          </ActionMenuItemContent>
        </div>
      )}
      subContentClassName="z-2 relative">
      <div className={cn(SUBMENU_CLASSNAME, submenuClassName)}>{children}</div>
    </DropdownMenuItemWithSubmenu>
  );
};

type AccountSubmenuRowProps = {
  row: AccountRow;
  title?: string;
  currency: string;
  precision: number;
  isBalancesHidden: boolean;
  withExternalIcon?: boolean;
  onSelect: (accountNumber: string) => void;
};

const AccountSubmenuRow = memo(function AccountSubmenuRow({
  row,
  title,
  currency,
  precision,
  isBalancesHidden,
  withExternalIcon,
  onSelect,
}: AccountSubmenuRowProps) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(row.accNum);
    },
    [row.accNum, onSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(row.accNum);
      }
    },
    [row.accNum, onSelect]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="flex items-center w-full h-[3.6rem] hover:bg-secondary-bg rounded-[0.8rem] px-[1.2rem] py-[0.8rem] cursor-pointer"
      aria-label={row.accNum}>
      <AccountRowContent
        row={row}
        title={title}
        currency={currency}
        precision={precision}
        isBalancesHidden={isBalancesHidden}>
        {withExternalIcon && (
          <OpenNewTabRoundedIcon className="size-[1.2rem] text-tertiary shrink-0" />
        )}
      </AccountRowContent>
    </div>
  );
});

export const AccountCardMenu = ({
  type,
  accountTitle,
  actionButtonsVisibility,
  onDeposit,
  onWithdraw,
  onInternalTransfer,
  onAccountTransfer,
  onOpenNewAccount,
  onExchange,
  onAccountClick,
  accounts,
  currency,
  precision,
  isBalancesHidden,
  triggerClassName,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [open, setOpen] = useState(false);
  const rows = useAccountRows(accounts);

  const closeAnd = useCallback(
    (fn: () => void) => () => {
      fn();
      setOpen(false);
    },
    []
  );

  const handleAccountClick = useCallback(
    (accountNumber: string) => {
      onAccountClick(accountNumber);
      setOpen(false);
    },
    [onAccountClick]
  );

  const handleTransferClick = useCallback(
    (accountNumber: string) => {
      onAccountTransfer(accountNumber);
      setOpen(false);
    },
    [onAccountTransfer]
  );

  const handleTradeClick = useCallback(
    (accountNumber: string) => {
      const row = rows.find(item => item.accNum === accountNumber);

      if (row) {
        openTradingAccUrl(row.tradingAccKey, type);
      }

      setOpen(false);
    },
    [rows, type]
  );

  const menuActions = useMemo<MenuAction[]>(
    () => [
      {
        key: 'deposit',
        visible: actionButtonsVisibility.deposit,
        icon: ArrowDownIcon,
        labelKey: 'personalAccount.overview.actions.deposit',
        onClick: onDeposit,
      },
      {
        key: 'exchange',
        visible: actionButtonsVisibility.exchange,
        icon: RefreshIcon,
        labelKey: 'personalAccount.overview.actions.exchange',
        onClick: onExchange,
      },
      {
        key: 'withdraw',
        visible: actionButtonsVisibility.withdraw,
        icon: ArrowUpRightIcon,
        labelKey: 'personalAccount.overview.actions.withdraw',
        onClick: onWithdraw,
      },
    ],
    [
      actionButtonsVisibility.deposit,
      actionButtonsVisibility.withdraw,
      actionButtonsVisibility.exchange,
      onDeposit,
      onWithdraw,
      onExchange,
    ]
  );

  const isWallet = type === 'ewallet';
  const hasSingleAccount = accounts.length === 1;
  const rowTitle = type === 'leverage' ? undefined : accountTitle;

  const renderAccountRows = (
    onSelect: (accountNumber: string) => void,
    withExternalIcon?: boolean
  ) =>
    rows.map(row => (
      <AccountSubmenuRow
        key={row.accNum}
        row={row}
        title={rowTitle}
        currency={currency}
        precision={precision}
        isBalancesHidden={isBalancesHidden}
        withExternalIcon={withExternalIcon}
        onSelect={onSelect}
      />
    ));

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        withChevron={false}
        className={cn(TRIGGER_CLASSNAME, triggerClassName)}>
        <EllipsisVerticalIcon className="size-[1.6rem] text-primary" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="bottom"
        collisionPadding={16}
        sticky="always"
        className={MENU_CONTENT_CLASSNAME}
        classNameViewport={MENU_VIEWPORT_CLASSNAME}>
        {menuActions.map(
          ({ key, visible, icon, labelKey, onClick }) =>
            visible && (
              <ActionMenuItem
                key={key}
                icon={icon}
                label={t(labelKey)}
                className={MENU_ITEM_CLASSNAME}
                childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
                iconClassName={MENU_ITEM_ICON_CLASSNAME}
                labelClassName={MENU_ITEM_LABEL_CLASSNAME}
                onClick={closeAnd(onClick)}
              />
            )
        )}

        {actionButtonsVisibility.trade &&
          accounts.length > 0 &&
          (hasSingleAccount ? (
            <ActionMenuItem
              icon={CandlesIcon}
              label={t('personalAccount.overview.actions.trade')}
              className={MENU_ITEM_CLASSNAME}
              childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
              iconClassName={MENU_ITEM_ICON_CLASSNAME}
              labelClassName={MENU_ITEM_LABEL_CLASSNAME}
              onClick={closeAnd(() =>
                openTradingAccUrl(String(accounts[0].tradingAccKey), type)
              )}
            />
          ) : (
            <AccountSubmenu
              icon={CandlesIcon}
              label={t('personalAccount.overview.actions.trade')}
              isMobile={isMobile}
              submenuClassName={ACCOUNTS_SUBMENU.className}
              submenuWidthRem={ACCOUNTS_SUBMENU.widthRem}>
              {renderAccountRows(handleTradeClick, true)}
            </AccountSubmenu>
          ))}

        {actionButtonsVisibility.transfer &&
          (isWallet ? (
            <AccountSubmenu
              icon={ArrowDownUpIcon}
              label={t('personalAccount.overview.actions.transfer')}
              isMobile={isMobile}
              submenuClassName={WALLET_TRANSFER_SUBMENU.className}
              submenuWidthRem={WALLET_TRANSFER_SUBMENU.widthRem}>
              <ActionMenuItem
                icon={ArrowDownUpIcon}
                labelIcon={<LogoSmallIcon className="size-[1.4rem] shrink-0" />}
                label={t('personalAccount.overview.actions.toWallet')}
                className={cn(MENU_ITEM_CLASSNAME, 'pr-[1.6rem]')}
                childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
                iconClassName={MENU_ITEM_ICON_CLASSNAME}
                labelClassName={cn(
                  MENU_ITEM_LABEL_CLASSNAME,
                  'whitespace-nowrap'
                )}
                onClick={closeAnd(() => onInternalTransfer('to'))}
              />
              <ActionMenuItem
                icon={ArrowDownUpIcon}
                labelIcon={<LogoSmallIcon className="size-[1.4rem] shrink-0" />}
                label={t('personalAccount.overview.actions.fromWallet')}
                className={cn(MENU_ITEM_CLASSNAME, 'pr-[1.6rem]')}
                childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
                iconClassName={MENU_ITEM_ICON_CLASSNAME}
                labelClassName={cn(
                  MENU_ITEM_LABEL_CLASSNAME,
                  'whitespace-nowrap'
                )}
                onClick={closeAnd(() => onInternalTransfer('from'))}
              />
            </AccountSubmenu>
          ) : accounts.length > 1 ? (
            <AccountSubmenu
              icon={ArrowDownUpIcon}
              label={t('personalAccount.overview.actions.transfer')}
              isMobile={isMobile}
              submenuClassName={ACCOUNTS_SUBMENU.className}
              submenuWidthRem={ACCOUNTS_SUBMENU.widthRem}>
              {renderAccountRows(handleTransferClick)}
            </AccountSubmenu>
          ) : (
            <ActionMenuItem
              icon={ArrowDownUpIcon}
              label={t('personalAccount.overview.actions.transfer')}
              className={MENU_ITEM_CLASSNAME}
              childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
              iconClassName={MENU_ITEM_ICON_CLASSNAME}
              labelClassName={MENU_ITEM_LABEL_CLASSNAME}
              onClick={closeAnd(() => onInternalTransfer())}
            />
          ))}

        {actionButtonsVisibility.openNewAccount && <MenuSeparator />}

        {actionButtonsVisibility.openNewAccount && (
          <ActionMenuItem
            icon={PlusIcon}
            label={t('personalAccount.overview.actions.addAccount')}
            className={MENU_ITEM_CLASSNAME}
            childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
            iconClassName={MENU_ITEM_ICON_CLASSNAME}
            labelClassName={MENU_ITEM_LABEL_CLASSNAME}
            onClick={closeAnd(onOpenNewAccount)}
          />
        )}

        {!isWallet &&
          accounts.length > 0 &&
          (hasSingleAccount ? (
            <ActionMenuItem
              icon={ApiTokensIcon}
              label={t('personalAccount.overview.actions.webApiTokens')}
              className={MENU_ITEM_CLASSNAME}
              childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
              iconClassName={MENU_ITEM_ICON_CLASSNAME}
              labelClassName={MENU_ITEM_LABEL_CLASSNAME}
              onClick={closeAnd(() => onAccountClick(accounts[0].accNum))}
            />
          ) : (
            <AccountSubmenu
              icon={ApiTokensIcon}
              label={t('personalAccount.overview.actions.webApiTokens')}
              isMobile={isMobile}
              submenuClassName={ACCOUNTS_SUBMENU.className}
              submenuWidthRem={ACCOUNTS_SUBMENU.widthRem}>
              {renderAccountRows(handleAccountClick)}
            </AccountSubmenu>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
